<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import {
		Alert,
		Button,
		ButtonV2,
		Container,
		Modal,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';

	import { unsubscribeCameraCloudStorage } from 'src/helpers/camera_cloud_storage_helpers';
	import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
	import {
		CameraCloudStorageSubscriptionStatus,
		type CameraCloudStorageActivePlan,
	} from 'src/types/CameraCloudStorage';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	/**
	 * Cancel-button entry for the Camera Cloud Storage subscription.
	 *
	 * Rendered alongside the Premium cancel button inside
	 * `CancelSubscription.svelte`. On success it dispatches `cancelled`
	 * so the host (PremiumSettings) can show the success modal before
	 * the Cancel Subscription collapse unmounts for a Cancelled plan.
	 */

	/** Allow callers (e.g. the section view) to override the plan source. */
	export let activePlan: CameraCloudStorageActivePlan | null = null;
	export let showCancelModal = false;

	const dispatch = createEventDispatcher<{ cancelled: void }>();

	let loading = false;

	$: ButtonComp = $showNewLayout ? ButtonV2 : Button;

	$: resolvedPlan = activePlan ?? $camera_cloud_storage.activePlan;
	$: isActive = resolvedPlan?.status === CameraCloudStorageSubscriptionStatus.Active;

	async function handleConfirmCancel() {
		loading = true;
		try {
			const res = await unsubscribeCameraCloudStorage();
			if (res.error) {
				toast.open({ type: 'danger', title: res.error });
			} else {
				// Host owns the success modal + store update so both survive
				// the Cancel Subscription collapse being torn down.
				dispatch('cancelled');
			}
		} finally {
			loading = false;
			showCancelModal = false;
		}
	}
</script>

{#if isActive && resolvedPlan}
	<svelte:component
		this={ButtonComp}
		class="width-md-auto width-sm-full"
		variant="danger"
		size="m"
		on:click={() => (showCancelModal = true)}
	>
		{$format('button_cancel_camera_cloud_storage_subscription', {
			default: 'Cancel Camera Cloud Storage',
		})}
	</svelte:component>

	{#if showCancelModal}
		<Modal
			on:close={() => (showCancelModal = false)}
			showModal={showCancelModal}
			title={$format('modal_title_cancel_camera_cloud_storage_subscription', {
				default: 'Cancel Camera Cloud Storage?',
			})}
		>
			<Container slot="content" padding="0">
				<Text class="mb-m">
					{$format('text_cancel_camera_cloud_storage_explainer', {
						default: `You are about to cancel your ${resolvedPlan.tier} Cameras plan. Your cameras will keep using Cloud Storage until the end of the billing period.`,
						values: { count: resolvedPlan.tier },
					})}
				</Text>
				<Alert
					type="warning"
					heading={$format('text_camera_cloud_storage_cancel_warning', {
						default:
							'After the billing period ends, cameras assigned to subscription slots will lose Cloud Storage. Your Premium-included slot (if any) is not affected.',
					})}
				/>
			</Container>
			<div slot="footer" class="d-flex justify-content-end gap-s">
				<svelte:component
					this={ButtonComp}
					variant="secondary"
					size="m"
					on:click={() => (showCancelModal = false)}
				>
					{$format('button_keep_camera_cloud_storage', {
						default: 'Keep subscription',
					})}
				</svelte:component>
				<svelte:component
					this={ButtonComp}
					variant="danger"
					size="m"
					{loading}
					disabled={loading}
					on:click={handleConfirmCancel}
				>
					{$format('modal_information_cancel_subscription', {
						default: 'Cancel subscription',
					})}
				</svelte:component>
			</div>
		</Modal>
	{/if}
{/if}
